(function () {
  'use strict';

  if (typeof window !== 'undefined') {
    if (window.__gcMuseumCommerceLoaded) {
      return;
    }
    window.__gcMuseumCommerceLoaded = true;
  }

  const DEFAULT_DIRECTION = 'created-descending';
  const VALID_DIRECTIONS = new Set([
    DEFAULT_DIRECTION,
    'created-ascending',
    'price-ascending',
    'price-descending'
  ]);
  const CART_SECTION_ID = 'cart-drawer-section';
  const UNAVAILABLE_COPY = 'THIS PIECE IS NO LONGER AVAILABLE.';
  const GENERIC_ADD_ERROR_COPY = 'THE ITEM COULD NOT BE ADDED. PLEASE TRY AGAIN.';
  const GENERIC_REMOVE_ERROR_COPY = 'THE ITEM COULD NOT BE REMOVED. PLEASE TRY AGAIN.';
  const ADDED_COPY = 'ADDED';
  const FREE_SHIPPING_MIN_CENTS = 4001;

  class GCCatalogLoadError extends Error {
    constructor(message) {
      super(message);
      this.name = 'GCCatalogLoadError';
    }
  }

  function normalizeDirection(direction) {
    return VALID_DIRECTIONS.has(direction) ? direction : DEFAULT_DIRECTION;
  }

  function toRecord(card, index) {
    if (card && card.dataset) {
      return {
        card,
        availableRank: card.dataset.available === 'true' ? 0 : 1,
        priceMinor: Number.parseInt(card.dataset.priceMinor, 10) || 0,
        publishedAt: Number.parseInt(card.dataset.publishedAt, 10) || 0,
        originalIndex: index
      };
    }

    return {
      card,
      availableRank: card.availableRank,
      priceMinor: card.priceMinor,
      publishedAt: card.publishedAt,
      originalIndex: card.originalIndex
    };
  }

  function compareRecords(left, right, direction) {
    if (left.availableRank !== right.availableRank) {
      return left.availableRank - right.availableRank;
    }

    if (direction.startsWith('created-') && left.publishedAt !== right.publishedAt) {
      const dateDirection = direction === 'created-descending' ? -1 : 1;
      return (left.publishedAt - right.publishedAt) * dateDirection;
    }

    if (direction.startsWith('price-') && left.priceMinor !== right.priceMinor) {
      const priceDirection = direction === 'price-descending' ? -1 : 1;
      return (left.priceMinor - right.priceMinor) * priceDirection;
    }

    return left.originalIndex - right.originalIndex;
  }

  function sortCards(cards, direction) {
    const normalizedDirection = normalizeDirection(direction);
    return Array.from(cards)
      .map(toRecord)
      .sort((left, right) => compareRecords(left, right, normalizedDirection))
      .map((record) => record.card);
  }

  function applySort(root, direction) {
    const grid = root.querySelector('[data-gc-grid]');
    if (!grid) {
      return [];
    }

    const cards = Array.from(grid.querySelectorAll('[data-product-card]'));
    const sortedCards = sortCards(cards, direction);
    sortedCards.forEach((card) => grid.appendChild(card));

    const soldOnly = root.querySelector('[data-gc-sold-only]');
    if (soldOnly) {
      const hasAvailableProduct = sortedCards.some(
        (card) => card.dataset.available === 'true'
      );
      soldOnly.hidden = sortedCards.length === 0 || hasAvailableProduct;
    }

    return sortedCards;
  }

  async function loadAllPages(root, fetchImpl = fetch) {
    const grid = root.querySelector('[data-gc-grid]');
    const pagination = root.querySelector('[data-gc-pagination]');
    const status = root.querySelector('[data-gc-status]');
    const sort = root.querySelector('[data-gc-sort]');
    const totalPages = Number.parseInt(root.dataset.totalPages, 10) || 1;

    if (!grid) {
      return [];
    }

    const firstPageCards = Array.from(grid.querySelectorAll('[data-product-card]'));
    const seenProductIds = new Set(
      firstPageCards.map((card) => card.dataset.productId)
    );
    const pendingCards = [];
    grid.setAttribute('aria-busy', 'true');

    try {
      for (let page = 2; page <= totalPages; page += 1) {
        const requestUrl = new URL(window.location.href);
        requestUrl.searchParams.set('page', String(page));
        requestUrl.searchParams.set('view', 'gc-cards');
        const response = await fetchImpl(requestUrl.toString());

        if (!response.ok) {
          throw new GCCatalogLoadError(
            'Catalog page ' + page + ' returned ' + response.status + '.'
          );
        }

        const parsedDocument = new DOMParser().parseFromString(
          await response.text(),
          'text/html'
        );
        const fetchedCards = parsedDocument.querySelectorAll('[data-product-card]');

        fetchedCards.forEach((card) => {
          const productId = card.dataset.productId;
          if (!seenProductIds.has(productId)) {
            seenProductIds.add(productId);
            pendingCards.push(card);
          }
        });
      }

      pendingCards.forEach((card) => grid.appendChild(card));
      const result = applySort(root, sort ? sort.value : DEFAULT_DIRECTION);
      grid.setAttribute('aria-busy', 'false');
      if (pagination) {
        pagination.hidden = true;
      }
      return result;
    } catch (error) {
      grid.setAttribute('aria-busy', 'false');
      if (status) {
        status.textContent = 'MORE PIECES COULD NOT BE LOADED.';
      }
      throw error;
    }
  }

  function initializeCatalog(root) {
    const sort = root.querySelector('[data-gc-sort]');
    if (!sort || root.dataset.gcCatalogBound === 'true') {
      return;
    }

    root.dataset.gcCatalogBound = 'true';
    const currentUrl = new URL(window.location.href);
    const initialDirection = normalizeDirection(
      currentUrl.searchParams.get('sort_by')
    );
    sort.value = initialDirection;
    applySort(root, initialDirection);

    sort.addEventListener('change', () => {
      const direction = normalizeDirection(sort.value);
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('sort_by', direction);
      window.history.replaceState({}, '', nextUrl);
      applySort(root, direction);
    });

    loadAllPages(root).catch(() => {});
  }

  function mediaFromButton(button) {
    return {
      src: button.dataset.src,
      srcset: button.dataset.srcset || '',
      alt: button.dataset.alt || '',
      index: Number.parseInt(button.dataset.index, 10) || 0
    };
  }

  function initGallery(root) {
    if (root.dataset.gcGalleryBound === 'true') {
      return;
    }

    root.dataset.gcGalleryBound = 'true';
    const thumbnails = Array.from(
      root.querySelectorAll('[data-gc-thumbnail]')
    );
    const mainImage = root.querySelector('[data-gc-main-image]');
    const mainTrigger = root.querySelector('[data-gc-main-trigger]');
    const dialog = root.querySelector('[data-gc-lightbox]');
    const lightboxImage = root.querySelector('[data-gc-lightbox-image]');
    const closeButton = root.querySelector('[data-gc-lightbox-close]');
    let returnFocusTarget = null;
    let transitionTimer = null;

    function copyImageData(image, media) {
      if (!image) {
        return;
      }

      image.src = media.src;
      image.alt = media.alt;
      if (media.srcset) {
        image.srcset = media.srcset;
      } else {
        image.removeAttribute('srcset');
      }
    }

    function updateMedia(media) {
      copyImageData(mainImage, media);
      copyImageData(lightboxImage, media);
    }

    thumbnails.forEach((button) => {
      button.addEventListener('click', () => {
        thumbnails.forEach((thumbnail) => {
          thumbnail.setAttribute('aria-selected', 'false');
        });
        button.setAttribute('aria-selected', 'true');

        const media = mediaFromButton(button);
        const prefersReducedMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

        if (prefersReducedMotion || !mainImage) {
          updateMedia(media);
          return;
        }

        if (transitionTimer !== null) {
          window.clearTimeout(transitionTimer);
        }
        mainImage.classList.add('is-changing');
        transitionTimer = window.setTimeout(() => {
          updateMedia(media);
          mainImage.classList.remove('is-changing');
          transitionTimer = null;
        }, 125);
      });
    });

    if (mainTrigger && dialog) {
      mainTrigger.addEventListener('click', () => {
        returnFocusTarget = mainTrigger;
        dialog.showModal();
      });
    }

    if (closeButton && dialog) {
      closeButton.addEventListener('click', () => {
        dialog.close();
      });
    }

    if (dialog) {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
          dialog.close();
        }
      });
      dialog.addEventListener('close', () => {
        if (returnFocusTarget) {
          returnFocusTarget.focus();
        }
      });
    }
  }

  function currentPathname() {
    if (
      typeof window !== 'undefined' &&
      window.location &&
      typeof window.location.pathname === 'string' &&
      window.location.pathname
    ) {
      return window.location.pathname;
    }

    return '/';
  }

  function requestHeaders() {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    };
  }

  async function safeJson(response) {
    try {
      return await response.json();
    } catch (_error) {
      return {};
    }
  }

  function resolveFetch(fetchImpl) {
    if (typeof fetchImpl === 'function') {
      return fetchImpl;
    }

    if (typeof globalThis.fetch === 'function') {
      return globalThis.fetch.bind(globalThis);
    }

    throw new Error(GENERIC_ADD_ERROR_COPY);
  }

  async function addOne(variantId, fetchImpl) {
    try {
      const response = await resolveFetch(fetchImpl)('/cart/add.js', {
        method: 'POST',
        headers: requestHeaders(),
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: [CART_SECTION_ID],
          sections_url: currentPathname()
        })
      });

      if (!response.ok) {
        if (response.status === 422) {
          throw new Error(UNAVAILABLE_COPY);
        }
        throw new Error(GENERIC_ADD_ERROR_COPY);
      }

      return await safeJson(response);
    } catch (error) {
      if (error && error.message === UNAVAILABLE_COPY) {
        throw error;
      }
      throw new Error(GENERIC_ADD_ERROR_COPY);
    }
  }

  async function removeLine(lineKey, fetchImpl) {
    try {
      const response = await resolveFetch(fetchImpl)('/cart/change.js', {
        method: 'POST',
        headers: requestHeaders(),
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: [CART_SECTION_ID],
          sections_url: currentPathname()
        })
      });

      if (!response.ok) {
        throw new Error(GENERIC_REMOVE_ERROR_COPY);
      }

      return await safeJson(response);
    } catch (_error) {
      throw new Error(GENERIC_REMOVE_ERROR_COPY);
    }
  }

  function normalizeCart(cart) {
    const source = cart || {};
    const normalized = { ...source };

    ['items', 'lines'].forEach((property) => {
      if (Array.isArray(source[property])) {
        normalized[property] = source[property].map((line) => ({
          ...line,
          quantity: line.quantity > 0 ? 1 : line.quantity
        }));
      }
    });

    return normalized;
  }

  function replaceCartDrawer(sectionHtml, documentRef) {
    const targetDocument =
      documentRef || (typeof document !== 'undefined' ? document : null);
    if (!targetDocument || !sectionHtml || !targetDocument.implementation) {
      return false;
    }

    const parsedDocument = targetDocument.implementation.createHTMLDocument('');
    parsedDocument.body.innerHTML = sectionHtml;
    const returnedWrapper = parsedDocument.querySelector(
      '#shopify-section-cart-drawer-section'
    );
    const currentWrapper = targetDocument.querySelector(
      '#shopify-section-cart-drawer-section'
    );

    if (!returnedWrapper || !currentWrapper) {
      return false;
    }

    const replacement = targetDocument.importNode
      ? targetDocument.importNode(returnedWrapper, true)
      : returnedWrapper;
    currentWrapper.replaceWith(replacement);
    return true;
  }

  function openCartDrawer(documentRef) {
    const targetDocument =
      documentRef || (typeof document !== 'undefined' ? document : null);
    const drawer = targetDocument
      ? targetDocument.querySelector('#cart-drawer')
      : null;

    if (!drawer || typeof drawer.open !== 'function') {
      return false;
    }

    drawer.open();
    return true;
  }

  function focusCartDrawer(documentRef) {
    const selectors = [
      '[data-gc-cart-conflict]:not([hidden])',
      '#cart-drawer [role="heading"]',
      '#cart-drawer h2',
      '#cart-drawer button',
      '#cart-drawer [href]'
    ];

    for (const selector of selectors) {
      const target = documentRef.querySelector(selector);
      if (!target || typeof target.focus !== 'function') {
        continue;
      }

      if (
        (selector.includes('conflict') || selector.includes('heading') || selector === '#cart-drawer h2') &&
        typeof target.setAttribute === 'function'
      ) {
        target.setAttribute('tabindex', '-1');
      }

      try {
        target.focus();
        return true;
      } catch (_error) {
        continue;
      }
    }

    return false;
  }

  function safelyUpdateDrawer(sectionHtml, documentRef) {
    try {
      const replaced = replaceCartDrawer(sectionHtml, documentRef);
      if (!replaced) {
        return false;
      }
      openCartDrawer(documentRef);
      focusCartDrawer(documentRef);
      return true;
    } catch (_error) {
      return false;
    }
  }

  function announce(button, copy) {
    try {
      const regionRoot = button.closest('.gc-card, .gc-product__info');
      const liveRegion = regionRoot ? regionRoot.querySelector('[aria-live]') : null;
      if (liveRegion) {
        liveRegion.textContent = copy;
      }
    } catch (_error) {
      return;
    }
  }

  function announceRemoval(button, copy) {
    try {
      const conflict = button.closest('[data-gc-cart-conflict]');
      const liveRegion = conflict
        ? conflict.querySelector('[data-gc-remove-status]')
        : null;
      if (liveRegion) {
        liveRegion.textContent = copy;
      }
    } catch (_error) {
      return;
    }
  }

  function drawerHtmlFromResponse(body) {
    return body && body.sections ? body.sections[CART_SECTION_ID] : '';
  }

  function addButtons(documentRef) {
    const targetDocument =
      documentRef || (typeof document !== 'undefined' ? document : null);
    if (!targetDocument || typeof targetDocument.querySelectorAll !== 'function') {
      return [];
    }

    return targetDocument.querySelectorAll(
      '[data-gc-quick-add], [data-gc-product-add]'
    );
  }

  function setAddButtonState(button, added) {
    if (!button || !button.dataset) {
      return false;
    }

    if (button.dataset.gcOriginalMarkup === undefined) {
      button.dataset.gcOriginalMarkup = button.innerHTML;
    }
    if (button.dataset.gcOriginalAriaLabel === undefined) {
      const originalAriaLabel =
        typeof button.getAttribute === 'function'
          ? button.getAttribute('aria-label')
          : '';
      button.dataset.gcOriginalAriaLabel = originalAriaLabel || 'ADD TO CART';
    }

    if (added) {
      button.textContent = ADDED_COPY;
      button.classList.add('is-added');
      button.setAttribute('aria-label', 'ADDED TO CART');
      button.setAttribute('aria-busy', 'false');
      button.disabled = true;
      delete button.dataset.gcSubmitting;
      return true;
    }

    if (button.dataset.gcSubmitting === 'true') {
      return false;
    }

    button.innerHTML = button.dataset.gcOriginalMarkup;
    button.classList.remove('is-added');
    button.setAttribute('aria-label', button.dataset.gcOriginalAriaLabel);
    button.setAttribute('aria-busy', 'false');
    button.disabled = false;
    delete button.dataset.gcSubmitting;
    return true;
  }

  function cartVariantIds(cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    return new Set(
      items
        .filter((item) => Number(item.quantity) > 0)
        .map((item) => Number.parseInt(item.variant_id || item.id, 10))
        .filter((variantId) => Number.isFinite(variantId))
    );
  }

  function updateCartBadge(cart, documentRef) {
    const targetDocument =
      documentRef || (typeof document !== 'undefined' ? document : null);
    if (!targetDocument || typeof targetDocument.querySelector !== 'function') {
      return false;
    }

    const cartIcon = targetDocument.querySelector('[data-testid="cart-icon"]');
    if (!cartIcon || typeof cartIcon.renderCartBubble !== 'function') {
      return false;
    }

    const directCount = Number.parseInt(cart && cart.item_count, 10);
    const itemCount = Number.isFinite(directCount) && directCount >= 0
      ? directCount
      : Array.from(cartVariantIds(cart)).length;
    const currentCount = Number.parseInt(cartIcon.currentCartCount, 10);
    if (currentCount === itemCount) {
      return true;
    }

    cartIcon.renderCartBubble(itemCount);
    return true;
  }

  function formatUsdCents(cents) {
    return `$${(Math.max(0, cents) / 100).toFixed(2)}`;
  }

  function shippingThresholdState(cart) {
    const parsed = Number.parseInt(cart && cart.total_price, 10);
    const subtotalCents = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    const qualifies = subtotalCents >= FREE_SHIPPING_MIN_CENTS;
    const remainingCents = qualifies ? 0 : FREE_SHIPPING_MIN_CENTS - subtotalCents;

    return {
      qualifies,
      subtotalCents,
      remainingCents,
      copy: qualifies
        ? 'YOU QUALIFY FOR FREE STANDARD SHIPPING'
        : `ADD ${formatUsdCents(remainingCents)} MORE FOR FREE SHIPPING`
    };
  }

  function renderShippingThreshold(cart, documentRef) {
    const targetDocument =
      documentRef || (typeof document !== 'undefined' ? document : null);
    if (!targetDocument || typeof targetDocument.querySelector !== 'function') {
      return false;
    }

    const host = targetDocument.querySelector('#cart-drawer .cart-drawer__summary');
    if (!host) {
      return false;
    }

    const state = shippingThresholdState(cart);
    let message = host.querySelector('[data-gc-shipping-threshold]');
    if (!message) {
      message = targetDocument.createElement('p');
      message.className = 'gc-shipping-threshold';
      message.dataset.gcShippingThreshold = '';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      host.prepend(message);
    }

    message.textContent = state.copy;
    message.classList.toggle('is-qualified', state.qualifies);
    return true;
  }

  function applyCartState(cart, documentRef) {
    const variantIds = cartVariantIds(cart);
    updateCartBadge(cart, documentRef);
    renderShippingThreshold(cart, documentRef);
    addButtons(documentRef).forEach((button) => {
      const variantId = Number.parseInt(button.dataset.variantId, 10);
      setAddButtonState(button, variantIds.has(variantId));
    });
    return variantIds;
  }

  async function fetchCart(fetchImpl) {
    const response = await resolveFetch(fetchImpl)('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) {
      throw new Error(GENERIC_ADD_ERROR_COPY);
    }
    return await safeJson(response);
  }

  async function syncCartState(documentRef, fetchImpl) {
    try {
      const cart = await fetchCart(fetchImpl);
      applyCartState(cart, documentRef);
      return true;
    } catch (_error) {
      return false;
    }
  }

  function updateVariantAddState(documentRef, variantId, added, fallbackButton) {
    let matched = false;
    addButtons(documentRef).forEach((button) => {
      if (Number.parseInt(button.dataset.variantId, 10) !== variantId) {
        return;
      }
      matched = true;
      setAddButtonState(button, added);
    });
    if (!matched && fallbackButton) {
      setAddButtonState(fallbackButton, added);
    }
  }

  function observeCartChanges(documentRef) {
    const targetDocument =
      documentRef || (typeof document !== 'undefined' ? document : null);
    const targetWindow =
      typeof window !== 'undefined' ? window : null;
    const guard = targetDocument ? targetDocument.documentElement : null;
    const Observer = targetWindow ? targetWindow.MutationObserver : null;
    if (
      !targetDocument ||
      !targetDocument.body ||
      !guard ||
      !Observer ||
      guard.dataset.gcCartObserverBound === 'true'
    ) {
      return false;
    }

    let timer;
    const scheduleSync = () => {
      if (timer && typeof targetWindow.clearTimeout === 'function') {
        targetWindow.clearTimeout(timer);
      }
      timer = targetWindow.setTimeout(() => {
        syncCartState(targetDocument);
      }, 120);
    };
    const touchesCartDrawer = (node) => {
      if (!node || node.nodeType !== 1) {
        return false;
      }
      return (
        (typeof node.matches === 'function' &&
          node.matches('#shopify-section-cart-drawer-section, #cart-drawer')) ||
        (typeof node.closest === 'function' &&
          node.closest('#shopify-section-cart-drawer-section, #cart-drawer')) ||
        (typeof node.querySelector === 'function' &&
          node.querySelector('#shopify-section-cart-drawer-section, #cart-drawer'))
      );
    };
    const observer = new Observer((mutations) => {
      const cartChanged = mutations.some((mutation) => (
        touchesCartDrawer(mutation.target) ||
        Array.from(mutation.addedNodes || []).some(touchesCartDrawer) ||
        Array.from(mutation.removedNodes || []).some(touchesCartDrawer)
      ));
      if (cartChanged) {
        scheduleSync();
      }
    });

    observer.observe(targetDocument.body, { childList: true, subtree: true });
    guard.dataset.gcCartObserverBound = 'true';
    return true;
  }

  function restoreAddButton(button, originalMarkup, wasDisabled) {
    button.innerHTML = originalMarkup;
    button.classList.remove('is-added');
    button.setAttribute('aria-busy', 'false');
    button.disabled = wasDisabled;
    delete button.dataset.gcSubmitting;
  }

  async function handleAdd(button, documentRef) {
    if (button.dataset.gcSubmitting === 'true' || button.classList.contains('is-added')) {
      return;
    }

    const variantId = Number.parseInt(button.dataset.variantId, 10);
    const originalMarkup = button.innerHTML;
    const wasDisabled = button.disabled;
    let succeeded = false;

    button.dataset.gcSubmitting = 'true';
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      const body = await addOne(variantId);
      updateVariantAddState(documentRef, variantId, true, button);
      announce(button, ADDED_COPY);
      succeeded = true;
      safelyUpdateDrawer(drawerHtmlFromResponse(body), documentRef);
      await syncCartState(documentRef);
    } catch (error) {
      announce(button, error.message);
    } finally {
      if (!succeeded) {
        restoreAddButton(button, originalMarkup, wasDisabled);
      }
    }
  }

  async function handleUnavailableRemoval(button, documentRef, lineKey) {
    const normalizedLineKey = (lineKey || button.dataset.lineKey || '').trim();
    if (!normalizedLineKey || button.dataset.gcSubmitting === 'true') {
      return;
    }

    const originalMarkup = button.innerHTML;
    const wasDisabled = button.disabled;
    button.dataset.gcSubmitting = 'true';
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      const body = await removeLine(normalizedLineKey);
      applyCartState(body, documentRef);
      safelyUpdateDrawer(drawerHtmlFromResponse(body), documentRef);
      const conflict = button.closest('[data-gc-cart-conflict]');
      if (conflict) {
        conflict.hidden = true;
      }
    } catch (error) {
      announceRemoval(button, error.message);
    } finally {
      button.innerHTML = originalMarkup;
      button.setAttribute('aria-busy', 'false');
      button.disabled = wasDisabled;
      delete button.dataset.gcSubmitting;
    }
  }

  function capInjectedQuantity(event) {
    const input = event.target;
    if (
      !input ||
      typeof input.matches !== 'function' ||
      !input.matches(
        '.gc-commerce [data-gc-quantity], [data-gc-cart-conflict] [data-gc-quantity]'
      )
    ) {
      return;
    }

    if (Number(input.value) > 1) {
      input.value = '1';
    }
  }

  function handleCartBackdropClick(event, documentRef) {
    const eventTarget = event && event.target;
    if (
      eventTarget &&
      typeof eventTarget.closest === 'function' &&
      eventTarget.closest('[data-testid="cart-drawer-trigger"]')
    ) {
      return false;
    }

    const drawer = documentRef.querySelector('#cart-drawer[open]');
    const dialog = drawer && drawer.querySelector('.theme-drawer__dialog[open]');
    if (!event || !dialog || typeof dialog.getBoundingClientRect !== 'function') {
      return false;
    }

    const rect = dialog.getBoundingClientRect();
    const outsidePanel = (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    );
    if (!outsidePanel) {
      return false;
    }

    const closeButton = documentRef.querySelector(
      '#cart-drawer .theme-drawer__close-button'
    );
    if (!closeButton || typeof closeButton.click !== 'function') {
      return false;
    }

    closeButton.click();
    return true;
  }

  function bindCommerce(documentRef) {
    const guard = documentRef.documentElement;
    if (!guard || guard.dataset.gcCommerceBound === 'true') {
      return;
    }

    guard.dataset.gcCommerceBound = 'true';
    documentRef.addEventListener('click', (event) => {
      if (handleCartBackdropClick(event, documentRef)) {
        event.preventDefault();
        return;
      }

      if (!event.target || typeof event.target.closest !== 'function') {
        return;
      }

      const addButton = event.target.closest(
        '[data-gc-quick-add], [data-gc-product-add]'
      );
      if (addButton) {
        event.preventDefault();
        return handleAdd(addButton, documentRef);
      }

      const removeButton = event.target.closest('[data-gc-remove-unavailable]');
      const lineKey = removeButton ? (removeButton.dataset.lineKey || '').trim() : '';
      if (removeButton && lineKey) {
        event.preventDefault();
        return handleUnavailableRemoval(removeButton, documentRef, lineKey);
      }
    });
    documentRef.addEventListener('input', capInjectedQuantity);
    documentRef.addEventListener('change', capInjectedQuantity);
    documentRef.addEventListener('submit', (event) => {
      const form = event.target;
      if (
        !form ||
        typeof form.closest !== 'function' ||
        !form.closest('.gc-commerce, [data-gc-cart-conflict]')
      ) {
        return;
      }

      form.querySelectorAll('[data-gc-quantity]').forEach((input) => {
        capInjectedQuantity({ target: input });
      });
    });
  }

  function recommendationNumber(value) {
    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : 0;
  }

  function score(currentProduct, candidateProduct) {
    const current = currentProduct || {};
    const candidate = candidateProduct || {};
    let total = 0;

    if (candidate.shape === current.shape) {
      total += 4;
    }

    const heightDifference = Math.abs(
      recommendationNumber(candidate.heightCm) - recommendationNumber(current.heightCm)
    );
    if (heightDifference <= 3) {
      total += 3;
    }

    const currentPrice = recommendationNumber(current.priceMinor);
    const priceDifference = Math.abs(
      recommendationNumber(candidate.priceMinor) - currentPrice
    );
    if (priceDifference <= currentPrice * 0.2) {
      total += 2;
    }

    if (recommendationNumber(candidate.recommendationPriority) > 0) {
      total += 1;
    }

    return total;
  }

  function rankedRecommendationGroup(currentProduct, candidates) {
    return (Array.isArray(candidates) ? candidates : [])
      .map((candidateProduct, originalIndex) => ({
        candidateProduct,
        originalIndex,
        score: score(currentProduct, candidateProduct)
      }))
      .filter(({ candidateProduct }) => (
        candidateProduct &&
        candidateProduct.available === true &&
        candidateProduct.id !== currentProduct.id
      ))
      .sort((left, right) => (
        right.score - left.score || left.originalIndex - right.originalIndex
      ));
  }

  function rankCandidates(currentProduct, sameCategoryCandidates, crossCategoryCandidates) {
    const current = currentProduct || {};
    const selected = [];
    const seenIds = new Set([current.id]);
    const groups = [sameCategoryCandidates, crossCategoryCandidates];

    groups.forEach((group) => {
      rankedRecommendationGroup(current, group).forEach(({ candidateProduct }) => {
        if (selected.length >= 3 || seenIds.has(candidateProduct.id)) {
          return;
        }

        seenIds.add(candidateProduct.id);
        selected.push(candidateProduct);
      });
    });

    return selected;
  }

  const GCGallery = {
    initGallery,
    mediaFromButton
  };

  const GCCommerce = {
    addOne,
    applyCartState,
    bindCommerce,
    fetchCart,
    handleCartBackdropClick,
    normalizeCart,
    observeCartChanges,
    openCartDrawer,
    renderShippingThreshold,
    removeLine,
    replaceCartDrawer,
    syncCartState,
    updateCartBadge,
    shippingThresholdState
  };

  const GCRecommendations = {
    rankCandidates,
    score
  };

  const GCCatalog = {
    GCCatalogLoadError,
    applySort,
    loadAllPages,
    sortCards,
    toRecord
  };
  GCCatalog.GCGallery = GCGallery;
  GCCatalog.GCCommerce = GCCommerce;
  GCCatalog.GCRecommendations = GCRecommendations;

  if (typeof window !== 'undefined') {
    window.GCCatalog = GCCatalog;
    window.GCGallery = GCGallery;
    window.GCCommerce = GCCommerce;
    window.GCRecommendations = GCRecommendations;
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('[data-gc-catalog]').forEach((root) => {
        if (root.querySelector('[data-gc-sort]')) {
          initializeCatalog(root);
        }
      });
      document.querySelectorAll('[data-gc-gallery]').forEach((root) => {
        initGallery(root);
      });
      bindCommerce(document);
      syncCartState(document);
      observeCartChanges(document);
      window.addEventListener('pageshow', () => {
        syncCartState(document);
      });
    });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = GCCatalog;
  }
})();
